export interface AppQuery {
  readonly engine: 'real' | 'fake';
  readonly moves: string[];
  readonly fen: string | null;
  /** `?onboarding=done` bypasses the wizard (mirrors `?engine=fake`). */
  readonly onboarding: 'done' | null;
  /**
   * `?dice=3-1,6-4,…` — scripted backgammon rolls, replayed in order and then
   * cycled. The audit/e2e surface needs deterministic dice the same way chess
   * needs `?engine=fake` + `?moves=`: a random roller makes no flow assertable.
   * Absent → real randomness.
   */
  readonly dice: string[];
  /** `?deal=42` - a fixed Bridge deal (seed), so audits and e2e see the same cards. Absent: random. */
  readonly deal?: number | null;
  /** `?bigcards=1` / `?bigcards=0` pins the Hearts and Rummy deck (tests and audits); absent: the stored choice, else the edition default. */
  readonly bigCards?: boolean | null;
  /**
   * `?target=20` - play a points game (Rummy, Hearts, Colour Dash) to this score instead of its own (100, 100, 500). A test and audit aid like
   * `deal`: a whole-game e2e that plays to the full target takes a minute or more on a loaded runner. A positive whole number, clamped to
   * TARGET_MIN..TARGET_MAX, ONE shared range that fits all three games (Colour Dash's own target of 500 is the largest); absent or invalid:
   * null, which means the game's own target.
   */
  readonly target?: number | null;
}

export const TARGET_MIN = 10;
export const TARGET_MAX = 5000;

export function parseQuery(): AppQuery {
  const url = new URL(window.location.href);
  return {
    engine: url.searchParams.get('engine') === 'fake' ? 'fake' : 'real',
    moves: (url.searchParams.get('moves') ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
    fen: url.searchParams.get('fen'),
    onboarding: url.searchParams.get('onboarding') === 'done' ? 'done' : null,
    dice: (url.searchParams.get('dice') ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
    deal: parseDeal(url.searchParams.get('deal')),
    bigCards: parseBigCards(url.searchParams.get('bigcards')),
    target: parseTarget(url.searchParams.get('target')),
  };
}

export function parseTarget(raw: string | null): number | null {
  if (raw === null || !/^\d{1,6}$/.test(raw)) return null;
  const n = Number(raw);
  if (n < 1) return null;
  return Math.min(TARGET_MAX, Math.max(TARGET_MIN, n));
}

export function parseBigCards(raw: string | null): boolean | null {
  return raw === '1' ? true : raw === '0' ? false : null;
}

function parseDeal(raw: string | null): number | null {
  if (raw === null || !/^\d{1,9}$/.test(raw)) return null;
  return Number(raw);
}

/**
 * Maps URL path → screen. The app is a SPA with `useState` for `screen`;
 * `App.tsx` pushes the matching path on every navigation (see `screenToPath`)
 * so the URL, the browser Back button, and share links all track the screen.
 * Initial state seeds from `window.location.pathname`, so a hard reload at
 * `/leaderboard?engine=fake` lands on the Leaderboard screen rather than Home.
 *
 * Screens that require additional state (game, lesson, online-game) are
 * mapped to `home` as a safe fallback — they're not addressable by URL, so
 * backing out of one lands on the nearest addressable ancestor.
 */
export type AppScreen =
  | 'home'
  | 'games'
  | 'game'
  | 'lobby'
  | 'learn'
  | 'lesson'
  | 'online-lobby'
  | 'online-game'
  | 'leaderboard'
  | 'parent'
  | 'settings'
  | 'story-harness';

const PATH_TO_SCREEN: Readonly<Record<string, AppScreen>> = {
  '/': 'home',
  '/games': 'games',
  '/leaderboard': 'leaderboard',
  '/parent': 'parent',
  '/settings': 'settings',
  '/learn': 'learn',
  '/lobby': 'lobby',
  '/online': 'online-lobby',
  '/__stories__': 'story-harness',
};

export function parseScreen(pathname: string): AppScreen {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  if (PATH_TO_SCREEN[normalized]) return PATH_TO_SCREEN[normalized];
  // Story harness accepts a storyId suffix (`/__stories__/button-primary`).
  // The exact path is a no-op in this app — the screen parses the id from
  // `window.location.pathname` itself — so any `/__stories__/...` route lands
  // on the story-harness screen.
  if (normalized === '/__stories__' || normalized.startsWith('/__stories__/')) {
    return 'story-harness';
  }
  return 'home';
}

/**
 * Canonical path for a screen, or `null` when the screen is not directly
 * addressable — it needs state a URL cannot carry (an in-flight game, a
 * selected lesson, an online game's room), or it parses its own path
 * (`story-harness` reads a story id off `window.location.pathname`).
 *
 * Derived from `PATH_TO_SCREEN` rather than written as a second literal, so
 * the two maps cannot drift. `query.test.ts` asserts the round trip.
 */
export function screenToPath(screen: AppScreen): string | null {
  if (NON_ADDRESSABLE.has(screen)) return null;
  for (const [path, mapped] of Object.entries(PATH_TO_SCREEN)) {
    if (mapped === screen) return path;
  }
  return null;
}

const NON_ADDRESSABLE: ReadonlySet<AppScreen> = new Set<AppScreen>([
  'game',
  'lesson',
  'online-game',
  // Addressable in PATH_TO_SCREEN, but its real path carries a story id that
  // this helper has no way to reconstruct — the screen reads it from the
  // location itself. Pushing a bare `/__stories__` would drop the id.
  'story-harness',
]);
